import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SiteProofLogo from '../../components/SiteProofLogo';
import { useLanguage } from '../../contexts/LanguageContext';

const EmailVerified = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  // Detect if arriving from the verification flow
  // Either via ?verified=1 query param (from SetupRoute fallback)
  // or via location.state.fromVerification (from auth-callback fallback)
  const queryParams = new URLSearchParams(location?.search);
  const isFromVerification =
    queryParams?.get('verified') === '1' ||
    location?.state?.fromVerification === true;

  const handleContinue = () => {
    if (isFromVerification) {
      // Send them to login — after login, auth-callback routing will determine
      // the correct setup step (company details if incomplete, plan selection if no subscription)
      navigate('/login?return_to=%2Fsetup-flow-company-details', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <SiteProofLogo size="lg" />
        </div>

        {/* Green checkmark */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <svg
              className="w-10 h-10 text-emerald-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>

        {isFromVerification ? (
          <>
            {/* Verification-flow specific messaging */}
            <h1 className="text-2xl font-bold text-white mb-3">
              Email verified!
            </h1>

            <p className="text-slate-300 text-sm leading-relaxed mb-2">
              Your email address has been successfully verified.
            </p>

            <p className="text-slate-400 text-sm leading-relaxed mb-8">
              Email verified. Please log in to continue.
            </p>

            {/* Step indicator */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-xs text-emerald-400 font-medium">Email verified</span>
              </div>
              <div className="w-8 h-px bg-slate-600" />
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-slate-700 border-2 border-slate-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs text-slate-400 font-bold">2</span>
                </div>
                <span className="text-xs text-slate-400 font-medium">Sign in</span>
              </div>
              <div className="w-8 h-px bg-slate-600" />
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-slate-700 border-2 border-slate-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs text-slate-500 font-bold">3</span>
                </div>
                <span className="text-xs text-slate-500 font-medium">Choose Plan</span>
              </div>
            </div>

            <button
              onClick={handleContinue}
              className="w-full py-3 px-6 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg transition-colors duration-200 text-sm"
            >
              Continue to sign in →
            </button>

            <p className="text-slate-500 text-xs mt-4">
              After signing in, you'll be taken directly to plan selection.
            </p>
          </>
        ) : (
          <>
            {/* Default messaging (e.g. navigated here directly) */}
            <h1 className="text-2xl font-bold text-white mb-3">
              {t('verified.heading')}
            </h1>

            <p className="text-slate-400 text-sm leading-relaxed mb-8">
              {t('verified.subtext')}
            </p>

            <button
              onClick={handleContinue}
              className="w-full py-3 px-6 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg transition-colors duration-200 text-sm"
            >
              {t('verified.cta')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerified;
